/**
 * SaveViewModal — Modal for saving or updating an analysis view.
 *
 * Two modes:
 * - Create: New view with name input and config summary
 * - Update: Choice to overwrite existing or save as new
 */
import { useState, useEffect } from 'react';
import { Modal, TextInput, Button, Group, Stack, Text, Box, Divider } from '@mantine/core';
import {
  IconChartLine,
  IconDroplet,
  IconCalendarEvent,
  IconLayoutRows,
  IconPercentage,
  IconLine,
} from '@tabler/icons-react';
import type { ViewConfig, AnalysisView } from '@/types/analysisView';
import { generateViewName } from '@/hooks/useAnalysisViews';

interface SaveViewModalProps {
  opened: boolean;
  onClose: () => void;
  config: ViewConfig;
  /** If set, the user modified this view and gets Update vs Save-as-new choice */
  activeView: AnalysisView | null;
  onSaveNew: (name: string) => void;
  onUpdate: (viewId: string) => void;
}

export function SaveViewModal({
  opened,
  onClose,
  config,
  activeView,
  onSaveNew,
  onUpdate,
}: SaveViewModalProps) {
  const [name, setName] = useState('');

  // Pre-fill name when modal opens
  useEffect(() => {
    if (opened) {
      setName(activeView ? '' : generateViewName(config));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);  // Only re-run when opened changes

  const handleSaveNew = () => {
    const trimmed = name.trim();
    if (trimmed) {
      onSaveNew(trimmed);
      onClose();
    }
  };

  const handleUpdate = () => {
    if (activeView) {
      onUpdate(activeView.id);
      onClose();
    }
  };

  const totalParams = config.parameters.length;
  const totalDosing = config.dosing.length;
  const totalEvents = config.events.length;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={activeView ? 'Update Saved View' : 'Save Analysis View'}
      size="sm"
    >
      <Stack gap="md">
        {/* Update existing option */}
        {activeView && (
          <>
            <Button
              variant="light"
              fullWidth
              onClick={handleUpdate}
            >
              Update &ldquo;{activeView.name}&rdquo;
            </Button>
            <Divider label="or save as new" labelPosition="center" />
          </>
        )}

        {/* Name input */}
        <TextInput
          label="View name"
          placeholder="e.g., Weekly Alkalinity Check"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          maxLength={50}
          description={`${name.length}/50`}
          data-autofocus
        />

        {/* Config summary */}
        <Box>
          <Text size="xs" fw={500} c="dimmed" mb={4}>
            This view saves:
          </Text>
          <Stack gap={4}>
            {totalParams > 0 && (
              <Group gap={6}>
                <IconChartLine size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs">
                  {totalParams} parameter{totalParams !== 1 ? 's' : ''}
                </Text>
              </Group>
            )}
            {totalDosing > 0 && (
              <Group gap={6}>
                <IconDroplet size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs">
                  {totalDosing} dosing chemical{totalDosing !== 1 ? 's' : ''}
                </Text>
              </Group>
            )}
            {totalEvents > 0 && (
              <Group gap={6}>
                <IconCalendarEvent size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs">
                  {totalEvents} event type{totalEvents !== 1 ? 's' : ''}
                </Text>
              </Group>
            )}
            <Group gap={6}>
              <IconLayoutRows size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs">
                {config.viewMode === 'stacked' ? 'Stacked view' : 'Combined view'}
              </Text>
            </Group>
            {config.viewMode === 'combined' && config.normalized && (
              <Group gap={6}>
                <IconPercentage size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs">Normalized mode</Text>
              </Group>
            )}
            {config.eventLines && (
              <Group gap={6}>
                <IconLine size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs">Event lines enabled</Text>
              </Group>
            )}
          </Stack>
          <Text size="xs" c="dimmed" mt="xs" fs="italic">
            Date range is not saved
          </Text>
        </Box>

        {/* Actions */}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSaveNew} disabled={!name.trim()}>
            {activeView ? 'Save as New' : 'Save View'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
